import { Box, Typography, Grid } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

import { FBoxContainer, FHButton, FSearch } from '../../../../components/base';
import TableHeaderGenerator from '../../../components/TableHeaderGenerator';
import AdminRowLoader from '../../../components/AdminRowLoader';
import { payorCredentialColumns } from '../../../configs/tableHeaders.config';
import { Roles } from '../../../../constants';
import { areUserRolesIn } from '../../../../helpers';
import {
  useProviderDetailsState,
  useProviderPayorCredentialsSection,
} from '../hooks';
import {
  PAYOR_CREDENTIALS_ADD_BUTTON_SX,
  PAYOR_CREDENTIALS_EMPTY_STATE_SX,
  PAYOR_CREDENTIALS_HEADER_SX,
  PAYOR_CREDENTIALS_SEARCH_SX,
} from '../styles';
import { ProviderPayorCredentialItem } from './ProviderPayorCredentialItem';

import type { ChangeEvent } from 'react';

export function ProviderPayorCredentials() {
  const { user } = useProviderDetailsState();
  const {
    payorCredentials,
    sortedPayorCredentials,
    credsLoading,
    credentialSearchStr,
    payorCredentialsSortField,
    payorCredentialsSortOrder,
    isNewCredentialAdding,
    editableCredentialIndex,
    handleAddCredential,
    handleCredentialSearch,
    sortPayorCredentials,
    handleEditCredential,
    handleCancelEditCredential,
    handleUpdateCredential,
    handleDeleteCredential,
  } = useProviderPayorCredentialsSection();

  if (
    !areUserRolesIn(user?.roles, [
      Roles.Admin,
      Roles.Biller,
      Roles.OnboardingCoordinator,
    ])
  ) {
    return null;
  }

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleCredentialSearch(e.target.value);
  };

  let credentialsContent;
  if (sortedPayorCredentials.length > 0) {
    credentialsContent = sortedPayorCredentials.map((credential, index) => (
      <ProviderPayorCredentialItem
        key={credential.id}
        credential={credential}
        index={index}
        isAdding={isNewCredentialAdding && index === 0}
        isEditing={editableCredentialIndex === index}
        isDisabled={credsLoading}
        onEdit={handleEditCredential}
        onCancel={handleCancelEditCredential}
        onSave={handleUpdateCredential}
        onDelete={handleDeleteCredential}
      />
    ));
  } else if (credsLoading) {
    credentialsContent = (
      <Box>
        {Array.from({ length: 3 }).map((_, index) => (
          <AdminRowLoader key={index} />
        ))}
      </Box>
    );
  } else {
    credentialsContent = (
      <Box sx={PAYOR_CREDENTIALS_EMPTY_STATE_SX}>
        <Typography variant="h6">No credentials found for this provider</Typography>
      </Box>
    );
  }

  return (
    <FBoxContainer sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Typography variant="h6" mb={2} fontWeight={700}>
          Payor provider credentials:
        </Typography>
        <Box sx={PAYOR_CREDENTIALS_HEADER_SX}>
          <FHButton
            sx={PAYOR_CREDENTIALS_ADD_BUTTON_SX}
            startIcon={<AddIcon />}
            disabled={isNewCredentialAdding}
            onClick={handleAddCredential}
          >
            Add new credential
          </FHButton>
          <FSearch
            sx={PAYOR_CREDENTIALS_SEARCH_SX}
            value={credentialSearchStr}
            autoComplete="off"
            inputHeight={40}
            placeholder="Credential name or payor"
            onChange={handleSearchChange}
          />
        </Box>
        <Grid container p={0.5} spacing={1} alignItems="flex-start">
          <Grid item xs={12}>
            {payorCredentials.length > 0 && (
              <TableHeaderGenerator
                columns={payorCredentialColumns}
                handleSorting={sortPayorCredentials}
                sortField={payorCredentialsSortField}
                sortOrder={payorCredentialsSortOrder}
              />
            )}
            {credentialsContent}
          </Grid>
        </Grid>
      </Box>
    </FBoxContainer>
  );
}
